/**
 * League data for the menus (Milestone 5, docs/game-design.md §7–8): the player's own league,
 * loaded from the game server on demand and kept for a little while.
 */
import type { GamePlan, MyLeague } from '@caged/shared';
import { create } from 'zustand';
import { DEV_GUEST, devGuestId, serverProgress } from '../net/config';
import { diag } from '../platform/diag';
import { useApp } from './store';

/** A loaded league is shown without asking again for this long (ms). */
const FRESH_MS = 30_000;

export interface LeagueState {
  mine: MyLeague | null;
  loading: boolean;
  failed: boolean;
  loadedAt: number;
  /** Loads the player's league (registered players only); `force` skips the freshness check. */
  load(force?: boolean): Promise<void>;
  /** Saves the game plan; false when it couldn't be saved. */
  savePlan(plan: GamePlan): Promise<boolean>;
  /** The season-end popups were shown. */
  seen(seasons: number[]): void;
  clear(): void;
}

/** Whoever is signed in can have a league; a guest can't (§7.7). */
function registered(): boolean {
  // Dev builds: a dev player whose id starts with "reg-" counts as registered (as on the server).
  if (DEV_GUEST) return devGuestId().startsWith('reg-');
  const a = useApp.getState().account;
  return !!a && a.signedIn && !a.guest;
}

export const useLeague = create<LeagueState>((set, get) => ({
  mine: null,
  loading: false,
  failed: false,
  loadedAt: 0,

  load: async (force = false) => {
    // Who this is must be known first: a registered player whose account hasn't loaded yet (or
    // couldn't) isn't a guest, and the guest invite would offer them Register.
    if (!DEV_GUEST && serverProgress() && !useApp.getState().account) {
      await useApp.getState().loadAccount();
      if (!useApp.getState().account) {
        set({ failed: true });
        return;
      }
    }
    if (!registered()) {
      set({ mine: { status: 'guest' }, failed: false });
      return;
    }
    const st = get();
    if (st.loading || (!force && st.mine && Date.now() - st.loadedAt < FRESH_MS)) return;
    set({ loading: true });
    try {
      const { fetchMyLeague } = await import('../net/leagueApi');
      const mine = await fetchMyLeague();
      set({ mine, loading: false, failed: false, loadedAt: Date.now() });
      // A finished season the player hasn't seen yet: its popup (shown on Home or Result).
      if (mine.status === 'ok' && mine.news.length) {
        const app = useApp.getState();
        const queued = new Set(app.popups.flatMap((p) => (p.kind === 'season' ? [p.season.season] : [])));
        const fresh = mine.news.filter((h) => !queued.has(h.season)).map((season) => ({ kind: 'season' as const, season }));
        if (fresh.length) useApp.setState({ popups: [...app.popups, ...fresh] });
      }
    } catch (err) {
      diag.error(`league: load failed: ${String(err)}`);
      set({ loading: false, failed: true });
    }
  },

  savePlan: async (plan) => {
    try {
      const { savePlanRemote } = await import('../net/leagueApi');
      const saved = await savePlanRemote(plan);
      const mine = get().mine;
      if (mine?.status === 'ok') set({ mine: { ...mine, plan: saved.plan } });
      return true;
    } catch (err) {
      diag.error(`league: plan not saved: ${String(err)}`);
      return false;
    }
  },

  seen: (seasons) => {
    const mine = get().mine;
    if (mine?.status === 'ok') set({ mine: { ...mine, news: mine.news.filter((h) => !seasons.includes(h.season)) } });
    void import('../net/leagueApi').then((api) => api.markSeasonsSeen(seasons)).catch(() => undefined);
  },

  clear: () => set({ mine: null, loadedAt: 0, failed: false }),
}));
